import { useEffect, useState } from "react";
import { useFetchData, useParseRouteActions } from "../../../helpers/GlobalHelper";
import BCModal from "../../ui/BCModal";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";
import LmsTable from "../ui/LmsTable";
import { LmsAccountingLoanTransactionsTransactionSplitOptions } from "../../constants/GlobalConstants";
import { guid } from "../../../Helpers";

const AllocatedCostsTable = ({ suffix }) => {
  return <LmsTable
    skipClick
    title="Allocated Costs"
    actionButtons={["export"]}
    apiSuffix={suffix}
    sortable={[
      "Loan Transaction", 
      "Display", 
      "Loan Property Unit Reconveyance",
      "$ Assigned per Unit",
      "% Assigned per Unit",
      "Number Of Units Split",
      "Include With Unit Payoff",
      "Row No",
      "Description",
      "Created On",
      "Created By",
      "Updated On",
      "Updated By",
      "Transaction Effective Date",
      "Property"
    ]}
    calculatedColumns={["allocatedCost"]}
    columnLabelsDictionary={{
      allocatedCost: "$ Assigned per Unit",
      allocatedPercent: "% Assigned per Unit",
      loanNumber: "Loan #"
    }}
    dataRules={[
      { field: "allocatedPercent", rule: "format", display: "percent" },
      { field: "allocatedCost", rule: "format", display: "dollar" },
    ]}
  />;
};

const MainTable = () => {
  const [isCheckNetSuiteDataOpen, setIsCheckNetSuiteDataOpen] = useState(false);
  const [checkedLoanTransactionId, setCheckedLoanTransactionId] = useState("");
  const actionButtons = [
    "add", 
    "edit", 
    "export",
    { 
      text: "Check NetSuite Data", 
      isDisabled: (checkedItems) => checkedItems?.length !== 1,
      handleClick: ({ firstCheckedRow }) => {
        setIsCheckNetSuiteDataOpen(true); 
        setCheckedLoanTransactionId(firstCheckedRow?.id);
      }
    }
  ];
  
  return <>
    {checkedLoanTransactionId && <BCModal
      isModalOpen={isCheckNetSuiteDataOpen}
      size="large"
      closeIcon
      onClose={() => setIsCheckNetSuiteDataOpen(false)}
      content={<>
        <LmsTable
          isDisabled
          apiSuffix={`loans/netsuite-data/${checkedLoanTransactionId}`}
        />
        <AllocatedCostsTable suffix={`loans/transaction-allocated-costs/${checkedLoanTransactionId}`} /> 
      </>}
    />}
    <LmsTable
      actionButtons={actionButtons}
      useApiFiltering
      apiSuffix="loans/loan-transactions"
      rowBackgroundColor="pink"
      rowBackgroundColorConditionFunc={(row) => row.voided === "Yes"}
      columnLabelsDictionary={{
        apiece: "A Piece", bpiece:"B Piece", transactionEffectiveDate: "Effective Date", borrowerOrganization: "Borrower", lppayment: "LP Payment", nwccdateInspected: "Inspection Company Date Inspected", loan: "Loan #", notionalBpiece: "Notional B Piece", outOfPocket: "Out Of Pocket(BC)"
      }}
    />
  </>;
};

const loanTransactionsFormFields = (myForm, isAdd = false, loanTransactionId = "") => {
  myForm.addGroupFields([
    myForm.getDropdownField(
      "Borrower",
      "metadata/list-borrower-organizations",
      { name: "borrowerOrganizationId", required: true, disabled: !isAdd }
    ),
    myForm.getDropdownField("Special Purpose Entity", "orgs/list-special-purpose-entities", { 
      name: "specialPurposeEntityId", 
      dependencyField: "borrowerOrganizationId",
      disabled: !isAdd
    }),
    myForm.getDropdownField(
      "Loan #",
      "loans/list-borrower-loans",
      {
        name: "loanId",
        disabled: !isAdd,
        required: true,
        optionalDropdownQueries: [
          { query: "specialPurposeEntityId", fieldName: "specialPurposeEntityId" },
          { query: "borrowerOrganizationId", fieldName: "borrowerOrganizationId" }
        ]
      }
    ),
    myForm.getDropdownField("Transaction Category", "metadata/list-transaction-categories", {
      required: true, 
      name: "transactionCategoryId",
      isHidden: { disabledTransactionEdit: true },
      isHiddenOperation: "notequal",
      disabled: true
    }),
    myForm.getDropdownField("Transaction Category", "loans/list-transaction-category", {
      required: true, 
      name: "transactionCategoryId",
      isHidden: { disabledTransactionEdit: true },
      isHiddenOperation: "equal"
    })
  ]);

  myForm.addGroupFields([
    myForm.getDropdownField("Transaction Type", "metadata/list-transaction-types-by-category", { 
      name:"transactionTypeId", 
      required: true, 
      dependencyField:"transactionCategoryId",
      isDisabledOn: { disabledTransactionEdit: true }
    }),
    myForm.getInputField("Transaction Amount", { fieldType: "dollar", required: true, disabled: !isAdd }),
    myForm.getCalendarField("Transaction Due Date", { isDefaultDateNow: isAdd, name: "transactionDueDate", disabled: !isAdd }),
    myForm.getCalendarField("Effective Date", { isDefaultDateNow: true, name: "transactionEffectiveDate", isDisabledOn: { disabledTransactionEdit: true } })
  ]);

  myForm.addGroupFields([
    ...(isAdd ? [myForm.getCheckboxField("Add to Loan Balance", { inline: true, value: true })] 
    : [myForm.getCheckboxField("Add to Loan Balance", { inline: true, disabled: true })]),
    myForm.getCheckboxField("Expect Payment", { name:"expectPayment", inline: true }),
    myForm.getInputField("Original Loan Amount", { name:"loanAmountOriginal", fieldType: "dollar", disabled: !isAdd }),
    myForm.getInputField("Current Loan Balance", { name:"loanAmountCurrent", fieldType: "dollar", disabled: !isAdd })
  ]);

  myForm.addCenteredGroup([myForm.getTextareaField("Description")]);
  myForm.addGroupFields([
    myForm.getLabelField("Link Property", { required: true, width: 2, isHidden: { disabledLinkPropertyOption: true }, isHiddenOperation: "notequal" }),
    myForm.getLabelField("Link Property", { width: 2, isHidden: { disabledLinkPropertyOption: true } }),
    myForm.getGroupRadioField("Yes", { isDisabledOn: { disabledLinkPropertyOption: true } }),
    myForm.getGroupRadioField("No", { isDisabledOn: { disabledLinkPropertyOption: true } }),
  ], {
    radioGroupName: "linkToPropertyOption",
    widths: 1,
    isCentered: true
  });

  myForm.addCenteredGroup([
    myForm.getDropdownField("Loan Properties", "loans/{id}/list-transaction-loan-properties", { 
      name: "loanPropertyIds", 
      dropdownMultiple: true,
      dependencyField:"loanId",
      isDisabledOn: { disabledLinkPropertyDropdown: true }
    }),
  ], { isHiddenFunc: ({ inputs }) => inputs?.linkToPropertyOption !== "Yes" || ( inputs?.linkToPropertyOption === "Yes" && inputs?.linkToLoanPropertyIdCsv === null) });

  myForm.addCenteredGroup([
    myForm.getCheckboxField("Voided", { name:"voided", inline: true, disabled: !isAdd })
  ]);

  if (!isAdd && myForm?.initialValues?.loanOwner !== "Builders Capital") {
    myForm.addCenteredGroup([
      myForm.getButtonField("Edit", undefined, { 
        width: 2, 
        isHidden: { loanOwner: "Builders Capital" },
        modalContent: (_, options) => <PurchasingCompanyPaymentLoanTransaction 
          loanTransactionId={loanTransactionId} 
          loanTransactionFormOptions={options}
        />
      }),
      myForm.getComponent((options) => <LmsTable
        apiSuffix={loanTransactionId ? `loans/pc-payment/${loanTransactionId}` : ""} 
        displayOnlyProps={["receivedOn", "receivedAmount"]}
        columnLabelsDictionary={{
          receivedOn: "Received On",
          receivedAmount: "Received Amt",
        }}
        refreshDataDependency={options?.inputs?.updatedPurchasingCompanyPayment}
        compact
        isDisabled
      />, { width: 14, isHidden: { loanOwner: "Builders Capital" } })
    ]);

    myForm.addDividerField();
    myForm.addGroupFields([
      myForm.getLabelField(`Owner- (A-${myForm?.initialValues?.apiecePercent} : B-${myForm?.initialValues?.bpiecePercent})`),
      myForm.getBlankField(),
      myForm.getBlankField(),
      myForm.getBlankField(),
    ]);
  }

  if (isAdd || (!isAdd && myForm?.initialValues?.loanOwner !== "Builders Capital")) {
    myForm.addGroupFields([
      myForm.getInputField("Custom Amount", { fieldType: "dollar", name:"customAmount" }),
      myForm.getDropdownField(
        "Transaction Split Option", LmsAccountingLoanTransactionsTransactionSplitOptions,
        { required: true, name: "transactionSplitOption", isDisabledOn: { disabledTransactionEdit: true } }
      ),
      myForm.getInputField("A Piece", { dependencyField:"loanId", fieldType: "dollar", disabled: true, name: "apiece" }),
      myForm.getInputField("B Piece", { fieldType: "dollar", disabled: true, name: "bpiece" })
    ], { isHidden: { loanOwner: "Builders Capital" } });
    
    myForm.addGroupFields([
      myForm.getInputField("Notional B Piece", { fieldType: "dollar", disabled: !isAdd }),
      myForm.getInputField("Current A Trans Balance", {  name: "aPieceCurrent", fieldType: "dollar", disabled: !isAdd }),
      myForm.getInputField("Current B Trans Balance", { name: "bPieceCurrent", fieldType: "dollar", disabled: !isAdd }),
      myForm.getInputField("Current B Notional Trans Balance", { name: "notionalBPieceCurrent", fieldType: "dollar", disabled: !isAdd })
    ], { isHidden: { loanOwner: "Builders Capital" } });
    
    myForm.addGroupFields([
      myForm.getInputField("Loan Purchaser Funded Balance", { name: "fundedBal", fieldType: "dollar", disabled: !isAdd }),
      myForm.getInputField("Difference of A Trans & Funded Bal", { name: "apieceFundedBalDiff", fieldType: "dollar", disabled: !isAdd }),
      myForm.getInputField("Funds Requested", { name: "fundsRequested", fieldType: "dollar"}),
      myForm.getBlankField()
    ], { isHidden: { loanOwner: "Builders Capital" } });
  }
      
  if (!isAdd && myForm?.initialValues?.loanOwner !== "Builders Capital") {
    myForm.addGroupFields([
      myForm.getLabelField("Payment Option", { width: 2 }),
      myForm.getGroupRadioField("New Payment"),
      myForm.getGroupRadioField("Existing Payment"),
    ], { radioGroupName: "paymentOption", widths: 1, isCentered: true });

    myForm.addCenteredGroup([
      myForm.getCurrencyField("Funds Received", { 
        width: 4, 
        required: true, 
        isHidden: { paymentOption: "" }, 
        isHiddenOperation: "equal", 
        name: "amountReceived" 
      }),
      myForm.getCalendarField("Funds Received On", { 
        width: 4, 
        name: "fundsReceivedOn",
        isHidden: { paymentOption: "New Payment" }, 
        isHiddenOperation: "notequal",
        required: { paymentOption: "New Payment" }
      }),
      myForm.getDropdownField("Purchasing Company Payment", "payment/list-purchasing-company-payments", { 
        width: 4, 
        dependencyField: "loanId",
        name: "purchasingCompanyPaymentId",
        isHidden: { paymentOption: "Existing Payment" }, 
        isHiddenOperation: "notequal"
      })
    ], { isHidden: { paymentOption: "" }, isHiddenOperation: "equal" });
  } 

  return myForm;
};

const getABPiece = async (inputs, fetchData) => {
  const transactionSplitOption = inputs?.transactionSplitOption || "Split";
  const transactionAmount = parseFloat(inputs?.transactionAmount || 0);
  const customAmount = parseFloat(inputs?.customAmount || 0);
  const { data } = await fetchData(`loans/abpiece?loanId=${inputs?.loanId}&transactionEffectiveDate=${inputs?.transactionEffectiveDate}&transactionSplitOption=${transactionSplitOption}&loantransactionId=${inputs?.id}`, { initLoadingState: true });
  if (data) {
    Object.keys(data).forEach(function(key) { if(data[key] === null) { data[key] = 0; }});
    const apiecePercent = data.apiece;
    const bpiecePercent = data.bpiece;
    const notionalBpiecePercent = data.notionalBpiece;
    if (customAmount === 0 && transactionSplitOption) {
      if (transactionSplitOption === "Split") {
        inputs.apiece = (transactionAmount * apiecePercent) / 100;
        inputs.bpiece = transactionAmount - inputs.apiece;
        inputs.notionalBPiece = (transactionAmount * notionalBpiecePercent) / 100;
      }
      else if (transactionSplitOption === "A Piece") {
        inputs.apiece = transactionAmount;
        inputs.bpiece = null;
        inputs.notionalBPiece = (transactionAmount * notionalBpiecePercent) / 100;
      }
      else if (transactionSplitOption === "B Piece") {
        inputs.apiece = null;
        inputs.bpiece = transactionAmount;
        inputs.notionalBPiece = null;
      }
    }
    else if (customAmount && transactionSplitOption) {
      if (transactionSplitOption === "Split") {
        inputs.apiece = (customAmount * apiecePercent) / 100;
        inputs.bpiece = transactionAmount - inputs.apiece;
      }
      else if (transactionSplitOption === "A Piece") {
        inputs.apiece = customAmount;
        inputs.bpiece = transactionAmount - customAmount;
      }
      else if (transactionSplitOption === "B Piece") {
        inputs.apiece = transactionAmount - customAmount;
        inputs.bpiece = customAmount;
      }

      inputs.notionalBPiece = (transactionAmount * notionalBpiecePercent) / 100;
    }
    else {
      inputs.apiece = null;
      inputs.bpiece = null;
      inputs.notionalBPiece = null;
    }

    if (inputs?.paymentOption === "Existing Payment") {
      if (transactionSplitOption === "A Piece") {
        inputs.amountReceived = transactionAmount;
      }
      else if (transactionSplitOption === "B Piece") {
        inputs.amountReceived = 0;
      }
      else {
        inputs.amountReceived = (transactionAmount * apiecePercent) / 100;
      }
    }
    // TODO: Sprint Work 14961: GET loans/abpiece fundedBal
    inputs.apiecePercent = apiecePercent;
    inputs.bpiecePercent = bpiecePercent;
    inputs.fundedBal = data.fundedBal;
    inputs.aPieceCurrent = data.currentApiece + parseFloat(inputs.apiece || 0);
    inputs.bPieceCurrent = data.currentBpiece + parseFloat(inputs.bpiece || 0);
    inputs.notionalBPieceCurrent = data.currentNotionalBpiece + parseFloat(inputs.notionalBPiece || 0);
    inputs.apieceFundedBalDiff = data.apieceFundedBalDiff + parseFloat(inputs.apiece || 0);
    inputs.fundsRequested = data.apieceFundedBalDiff+ parseFloat(inputs.apiece || 0);
    return inputs;
  }
};

const setInputData = async (options) => {
  const loanId = options?.inputs?.loanId;
  const inputs = options?.inputs;
  const { data: loanDetails } = await options?.fetchData(`loans/${loanId}/details`);
  if (loanDetails?.borrowerOrganizationId) {
    inputs.loanAmountOriginal = loanDetails?.loanAmountOriginal;
    inputs.borrowerOrganizationId = loanDetails?.borrowerOrganizationId;
    inputs.loanAmountCurrent = loanDetails?.loanAmountCurrent;
    inputs.loanOwner = loanDetails?.loanOwner;
  }

  await getABPiece(inputs, options?.fetchData);
  options?.setInputs({ ...inputs });
};

const handleOnLoanTransactionsFormChange = async (options, lastUpdated) => {
  // TODO: more to add after Sprint Work 14945
  const inputs = options?.inputs;
  if (lastUpdated === "loanId" && inputs?.loanId ) {
    await setInputData(options);
  }

  if (lastUpdated === "borrowerOrganizationId" && inputs?.borrowerOrganizationId) {
    options?.setInputs({ ...inputs, loanId: undefined });
  }  

  if (lastUpdated === "customAmount") {
    getABPiece(inputs, options?.fetchData);
  }

  if (lastUpdated === "transactionTypeId" && inputs?.transactionTypeId) {
    const { data: transType } = await options?.fetchData(`metadata/transaction-type/${inputs?.transactionTypeId}`);
    options?.setInputs({
      ...inputs, 
      expectPayment: transType?.expectPayment,
      linkToPropertyOption: transType?.linkToPropertyOption === "Optional" ? "Yes" : transType?.linkToPropertyOption,
      transactionSplitOption: transType?.transactionSplitOption,
      disabledLinkPropertyOption: transType?.linkToPropertyOption !== "Optional"
    });
  }

  if (lastUpdated === "transactionSplitOption") {
    inputs?.loanId ? await setInputData(options) : undefined;
  }

  if (lastUpdated === "transactionEffectiveDate") {
    const inputDate = new Date(inputs.transactionEffectiveDate);
    const today = new Date();
    const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    if (inputDate < currentMonth) {
      options?.setAlertMessage("Transaction Date is for the previous, open, month. Do you want to proceed?", () => getABPiece(inputs, options?.fetchData));
    }
  }

  if (lastUpdated === "paymentOption") {
    if (inputs.paymentOption === "New Payment") {
      inputs.purchasingCompanyPaymentId = "";
    }
    else if(inputs.paymentOption === "Existing Payment") {
      inputs.fundsReceivedOn = "";
      if (inputs?.transactionSplitOption === "A Piece") {
        inputs.amountReceived = parseFloat(inputs?.transactionAmount || 0);
      }
      else if (inputs?.transactionSplitOption === "B Piece") {
        inputs.amountReceived = 0;
      }
      else {
        inputs.amountReceived = (parseFloat(inputs?.transactionAmount || 0) * inputs?.apiecePercent) / 100;
      }
    }
  }
};

const AddLoanTransactionsForm = () => {
  const myForm = new BCFormController({}, { submitSuffix: "loans/transaction", blankWidth: 4 });
  loanTransactionsFormFields(myForm, true);
  const formActions = ["save-continue", "save-close", "close"];
  return (<LmsFormGenerator myForm={myForm}  actions={formActions} onFormChanged={handleOnLoanTransactionsFormChange} />);
};

const EditPurchasingCompanyPaymentForm = ({ id, loanTransactionId, setIsEdit, loanTransactionFormOptions }) => {
  const [{ data: purchaseCompanyData, isLoading, fetchData }] = useFetchData(`payment/purchasing-company-payment-loan-transactions/${id}`);
  const [loanTransactionDetails, setLoanTransactionDetails] = useState(undefined as any);
  const onEditSuccess = (responseData) => {
    if (loanTransactionFormOptions) {
      loanTransactionFormOptions.setInputs({ ...loanTransactionFormOptions.inputs, updatedPurchasingCompanyPayment: responseData?.id || guid() });
    }
  };

  const myForm = new BCFormController(purchaseCompanyData, {
    submitMethod: "PUT",
    submitSuffix: "payment/purchasing-company-payment-loan-transaction",
    deleteSuffix: "payment/purchasing-company-payment-loan-transactions",
    blankWidth: 4,
    values: {
      purchasingCompanyPaymentId: purchaseCompanyData?.purchasingCompanyPaymentId,
      loanTransactionId: purchaseCompanyData?.loanTransactionId,
      loanTransactionDetails: loanTransactionDetails
    },
    dataFormatterFunc:  (data) => { return { 
      id: id,
      purchasingCompanyPaymentId: purchaseCompanyData?.purchasingCompanyPaymentId,
      loanTransactionId: purchaseCompanyData?.loanTransactionId,
      transactionAmount: data?.transactionAmount,
      description: data?.description,
    }; },
    onSubmitSuccess: onEditSuccess
  });

  const formActions = ["save", "save-close", "delete", "close"];
  myForm.addCenteredGroup([myForm.getDropdownDisplayField("Purchasing Company Payment", {
    name:"purchasingCompanyPaymentDisplay"
  })]);

  myForm.addCenteredGroup([myForm.getDropdownDisplayField("Loan Transaction", {
    name:"loanTransactionDisplay"
  })]);

  if (myForm?.values?.loanTransactionDetails) {
    myForm.addCenteredGroup([
      myForm.getLabelField("A Piece: $"),
      myForm.getWarningTextField(`${myForm?.values?.loanTransactionDetails?.apiece}`)
    ]);

    myForm.addCenteredGroup([
      myForm.getLabelField("Funded Amount: $"),
      myForm.getWarningTextField(`${myForm?.values?.loanTransactionDetails?.fundedAmount}`)
    ]);

    myForm.addCenteredGroup([
      myForm.getLabelField("Pending Amount: $"),
      myForm.getWarningTextField(`${myForm?.values?.loanTransactionDetails?.amount}`)
    ]);
  }

  myForm.addCenteredGroup([myForm.getInputField("Transaction Amount", { fieldType: "dollar", required: true })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Description")]);
  
  useEffect(() => {
    if (purchaseCompanyData) {
      const getLoanTransactionDetails = async () => {
        const { data: loanTransaction } = await fetchData(loanTransactionId ? `loans/loan-transaction-details/${loanTransactionId}` : "", { fetchMethod: "GET" });
        setLoanTransactionDetails(loanTransaction);
      };
    
      getLoanTransactionDetails();
    }
  }, [purchaseCompanyData]);

  return (<LmsFormGenerator
    myForm={myForm}
    actions={formActions}
    handleClose={() => setIsEdit(false)}
    isLoading={isLoading}
  />);
};

const PurchasingCompanyPaymentLoanTransaction = ({ loanTransactionId, loanTransactionFormOptions }) => {
  const [purchasingCompanyPaymentId, setPurchasingCompanyPaymentId] = useState("");
  const [isEditPurchasingCompanyPayment, setIsEditPurchasingCompanyPayment] = useState(false);
  return <>
    {!isEditPurchasingCompanyPayment && <LmsTable
      title="Purchasing Company Payment Loan transaction" 
      apiSuffix={`payment/${loanTransactionId}/purchasing-company-payment-loan-transactions`}
      skipCache={true}
      deleteSuffix="payment/purchasing-company-payment-loan-transactions"
      actionButtons={[
        { 
          text: "Edit", 
          isDisabled: (checkedItems) => checkedItems?.length !== 1,
          handleClick: ({ firstCheckedRow }) => {setIsEditPurchasingCompanyPayment(true); setPurchasingCompanyPaymentId(firstCheckedRow.id);}
        },
        "remove", 
        "export"]
      }
      onClickModalContent={(setIsModalOpen, { clickedRowData }) => <EditPurchasingCompanyPaymentForm 
        id={clickedRowData?.id} 
        loanTransactionId={loanTransactionId} 
        setIsEdit={setIsModalOpen}
        loanTransactionFormOptions={loanTransactionFormOptions}
      />}
      columnClickOptions={{
        display: {
          idProp: "id",
          isModalContentLink: true,
        }
      }}
      refreshDataDependency={loanTransactionFormOptions?.inputs?.updatedPurchasingCompanyPayment}
    />}
    {isEditPurchasingCompanyPayment && <EditPurchasingCompanyPaymentForm 
      id={purchasingCompanyPaymentId} 
      loanTransactionId={loanTransactionId} 
      setIsEdit={setIsEditPurchasingCompanyPayment}
      loanTransactionFormOptions={loanTransactionFormOptions}
    />}
  </>;
};

export const EditLoanTransactionsForm = ({ loanTransactionId, handleClose = undefined as any }) => {
  const [updatedData, setUpdatedData] = useState({} as any);
  const [isAbPieceLoading, setIsAbPieceLoading] = useState(true);
  const [{ data: loanTransactionData, isLoading, error, fetchData }] = useFetchData(`loans/loan-transaction/${loanTransactionId}`);
  const updateInputData = async () => {
    const loanId = loanTransactionData?.loanId;
    const { data: loanDetails } = await fetchData(`loans/${loanId}/details`);
    if (loanDetails?.borrowerOrganizationId) {
      const abPieceData = await getABPiece(loanTransactionData, fetchData);
      if (abPieceData) {
        setIsAbPieceLoading(false);
        setUpdatedData({
          ...abPieceData,
          loanAmountOriginal: loanDetails?.loanAmountOriginal, 
          borrowerOrganizationId: loanDetails?.borrowerOrganizationId, 
          loanAmountCurrent: loanDetails?.loanAmountCurrent,
          loanSubStatus: loanDetails?.loanSubStatus || ""
        });
      }
    }
  };

  useEffect(() => {
    if (loanTransactionData?.loanId) {
      updateInputData();
    }
  }, [loanTransactionData?.loanId]);

  const updatedLoanTransactionData = { ...loanTransactionData, ...updatedData };
  const myForm = new BCFormController(updatedLoanTransactionData, {
    values: { 
      ...updatedData,
      loanOwner: loanTransactionData?.loanOwner,
      disabledLinkPropertyOption: loanTransactionData?.linkToPropertyOption !== "Optional",
      disabledLinkPropertyDropdown: loanTransactionData?.linkToLoanPropertyIdCsv?.length > 0,
      linkToLoanPropertyIdCsv: loanTransactionData?.linkToLoanPropertyIdCsv,
      disabledTransactionEdit: (loanTransactionData?.transactionCategoryId === "e7dad59c-0a7a-456b-8506-7aa92ea04db0" || loanTransactionData?.transactionCategoryId === "fc315810-8264-47db-afa5-a67a00e426e4")
    },
    submitSuffix: "loans/update-loan-transaction",
    blankWidth: 4,
    displayMessageLocally: true,
    excludeFromSubmit: ["disabledLinkPropertyOption"]
  });
  loanTransactionsFormFields(myForm, false, loanTransactionId);
  // TODO: unhide it when applying permission
  // const handleCreditFeeButton = async (_, handleSubmit, options) => {
  //   // if (user role has tc credit fee only) { see Sprint Work 14833 for more
  //   // const { error: validateTcCreditFee, data: validatedData } = await fetchData(`payment/validate-tc-credit-fee/${loanTransactionId}`, { fetchMethod: "POST" });
  //   // }
  //   const { error, data } = await fetchData(`payment/refund/${loanTransactionId}`, { fetchMethod: "POST" });
  //   if (!error && data) {
  //     handleSubmit(`payment/refund/${loanTransactionId}`, { method: "PUT", data: { 
  //       id: data.id, 
  //       loanTransactionId: loanTransactionId, 
  //       ...(loanTransactionData?.amountReceivedOn ? { receivedDate: loanTransactionData?.amountReceivedOn } : {}) 
  //     }, buttonText: options.buttonText });
  //   }
  //   else {
  //     options?.useFormInstance?.setFormErrorMessage(error);
  //   }
  // };

  // const handleUndoCreditFeeButton = async (_, handleSubmit, options) => {
  //   handleSubmit(`payment/undo-credit-fee/${loanTransactionId}`, { method: "PUT", data: {}, buttonText: options.buttonText });
  // };

  const formActions = [
    "save", 
    "save-close", 
    // TODO: hiding button for bug 14424, future logic open for users with InternalName.ToLower() == "credit fee"
    // { 
    //   text: "Credit Fee", 
    //   handleClick: handleCreditFeeButton, 
    //   isDisabled: () => loanTransactionData?.refundOfLoanTransactionId || loanTransactionData?.refundedOn || loanTransactionData?.isActive === false || updatedData?.loanSubStatus === "Lost"
    // },
    // { 
    //   text: "Undo Credit Fee", 
    //   handleClick: handleUndoCreditFeeButton,
    //   isDisabled: () => !loanTransactionData?.refundOfLoanTransactionId || !loanTransactionData?.refundedOn || loanTransactionData?.isActive === true || updatedData?.loanSubStatus === "Lost"
    // },
    "delete", 
    "close"
  ];
  
  return <>
    <LmsFormGenerator
      myForm={myForm}
      isLoading={isLoading || isAbPieceLoading}
      error={error}
      actions={formActions}
      onFormChanged={handleOnLoanTransactionsFormChange}
      handleClose={handleClose}
    />
    <AllocatedCostsTable suffix={`loans/transaction-allocated-costs/${loanTransactionId}`} />
  </>;
};

const LmsAccountingLoanTransactions = ({ params = {}, history }) => {
  const { isMainPage, isAdd, isEdit, isActionAllowed, id } = useParseRouteActions(params);
  if (!isActionAllowed) {
    history.goBack();
  }

  return (<>
    {isMainPage && <MainTable />}
    {isAdd && <AddLoanTransactionsForm />}
    {isEdit && <EditLoanTransactionsForm loanTransactionId={id} />}
  </>);
};

export default LmsAccountingLoanTransactions;